/**
 * Asynchrone Domänenereignisse über die transaktionale Outbox ([V] 3.3, E-n Nr. 2 und 6; Anhang A.events).
 *
 * Nutzlast nur IDs und Zähler (nie Klartext, [A] 4.1 Grundregel Redaktion): deshalb `z.strictObject`
 * je Variante und für `sourceRef` (R-5) – ein unbekannter Schlüssel wird abgelehnt statt still entfernt.
 * Zustellung je Abonnent UND Empfänger (`EventDelivery`, RV-10); Abonnenten überspringen nicht mehr
 * vorhandene IDs ohne Fehler (E-n Nr. 6).
 */
import { z } from 'zod';
import type { DbTx, DomainContext } from './common.js';

const Ids = z.array(z.uuid()).max(500);

export const DomainEvent = z.discriminatedUnion('type', [
  // messages.ingested: auch erneut mit derselben messageId bei jeder Erhöhung von message.content_revision (E-n Nr. 7)
  z.strictObject({
    type: z.literal('messages.ingested'),
    userId: z.uuid(),
    messageIds: Ids,
    conversationIds: Ids,
  }),
  z.strictObject({
    type: z.literal('interactions.ingested'),
    userId: z.uuid(),
    interactionIds: Ids,
  }),
  z.strictObject({
    type: z.literal('relationship.recomputed'),
    userId: z.uuid(),
    personIds: Ids,
  }),
  z.strictObject({
    type: z.literal('person.updated'),
    userId: z.uuid(),
    personId: z.uuid(),
    revision: z.int(),
  }),
  // v12 (V12-29): jede Transaktion, die person_id einer bereits veröffentlichten Interaktion ändert (person_revision + 1)
  z.strictObject({
    type: z.literal('interactions.reassigned'),
    userId: z.uuid(),
    interactionIds: Ids,
  }),
  // v12 (V12-30): Abschlussabsicht aus der Sendeerkennung; Abonnent `operations` ruft `completeFromSource`
  z.strictObject({
    type: z.literal('operation.completion_requested'),
    userId: z.uuid(),
    operationId: z.uuid(),
    source: z.enum(['send_detected', 'user_confirmed']),
    interactionId: z.uuid().optional(),
  }),
  z.strictObject({ type: z.literal('persons.merged'), sourceId: z.uuid(), targetId: z.uuid() }),
  z.strictObject({
    type: z.literal('account.created'),
    userId: z.uuid(),
    connectedAccountId: z.uuid(),
    connectorId: z.string(),
  }),
  z.strictObject({
    type: z.literal('snapshot.completed'),
    accountId: z.uuid(),
    stream: z.string(),
    fetchRunId: z.uuid(),
  }),
  z.strictObject({
    type: z.literal('radar_item.upserted'),
    userId: z.uuid(),
    radarItemId: z.uuid(),
  }),
  z.strictObject({
    type: z.literal('task.completed'),
    userId: z.uuid(),
    taskId: z.uuid(),
    sourceRef: z.strictObject({ type: z.string(), id: z.string() }),
  }),
]);
export type DomainEvent = z.infer<typeof DomainEvent>;
export type DomainEventType = DomainEvent['type'];

export interface EventOutbox {
  /** Validiert strikt und liefert die Ereignis-ID (UUIDv7). */
  publish(tx: DbTx, event: DomainEvent): Promise<string>;
}

export interface EventSubscriber<T extends DomainEventType> {
  name: string;
  type: T; // ctx = SystemContext des Empfängers (je Empfänger eine Zustellung, E-n Nr. 6)
  handle(
    ctx: DomainContext,
    event: Extract<DomainEvent, { type: T }>,
    eventId: string,
  ): Promise<void>;
}

/** Setzt die Zustellung (eventId, subscriber, ctx.userId) von running auf done – in der Transaktion der Fachwirkung (RV-10). */
export interface EventDelivery {
  markDelivered(
    tx: DbTx,
    ctx: DomainContext,
    eventId: string,
    subscriber: string,
  ): Promise<boolean>;
}
